import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStatusContext } from '../hooks/SubscriptionStatusContext';
import { useAdvancedAnimations } from '../hooks/useAdvancedAnimations';
import { easings } from '../styles/animations';

// Styles
import { GlobalStyle } from './styles/globalStyles';

// Components
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import Humanism from './components/Humanism';
import Integrations from './components/Integrations';
import Technology from './components/Technology';
import CodeShowcase from './components/CodeShowcase';
import Comparison from './components/Comparison';
import Security from './components/Security';
import Pricing from './components/Pricing';
import Enterprise from './components/Enterprise';
import FAQ from './components/FAQ';
import CTA from './components/CTA';
import Footer from './components/Footer';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { status } = useSubscriptionStatusContext();

  // Hooks de animação avançados
  const heroAnimation = useAdvancedAnimations<HTMLDivElement>({
    direction: 'up',
    distance: 40,
    delay: 0,
    duration: 800,
    easing: easings.smooth,
  });

  const featuresAnimation = useAdvancedAnimations<HTMLDivElement>({
    direction: 'up',
    distance: 30,
    delay: 100,
    duration: 600,
    easing: easings.smooth,
  });

  // Redireciona usuários com assinatura ativa para /home
  const hasValidSubscription = status.plano &&
    status.plano.trim() !== '' &&
    status.plano !== 'null';

  useEffect(() => {
    const activationPending = (() => {
      try { return localStorage.getItem('loopra_enterprise_activation_pending') === '1'; } catch { return false; }
    })();
    if (!status.loading && hasValidSubscription && !activationPending) {
      console.log('[DEBUG] Usuário com assinatura ativa tentando acessar landing, redirecionando para /home');
      navigate('/home', { replace: true });
    }
  }, [status.loading, hasValidSubscription, navigate]);

  // Se ainda está carregando status, não renderiza nada (App loading cuida)
  if (status.loading) {
    return null;
  }

  // Se tem assinatura válida, não renderiza landing (vai redirecionar)
  if (hasValidSubscription) {
    return null;
  }

  // Renderiza landing page normalmente
  return (
    <>
      <GlobalStyle />
      {/* Navbar renderizada via portal diretamente no body para garantir position: fixed correto */}
      {createPortal(<Navbar />, document.body)}
      <div ref={heroAnimation.ref} style={heroAnimation.animationStyles}>
        <Hero />
      </div>
      <div ref={featuresAnimation.ref} style={featuresAnimation.animationStyles}>
        <Features />
      </div>
      <Humanism />
      <Integrations />
      <Technology />
      <CodeShowcase />
      <Comparison />
      <Security />
      <Pricing />
      <Enterprise />
      <FAQ />
      <CTA />
      <Footer />
    </>
  );
};

export default LandingPage;
